import { useNavigate } from "react-router";
import { ArrowLeft, CheckCircle } from "lucide-react";
import { Logo } from "../components/Logo";

export function POPIAPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#F8F6F4] flex flex-col">

      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-white border-b border-[#E5E7EB]">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 h-14 flex items-center gap-4">
          <button onClick={() => navigate("/")} className="flex items-center gap-2.5 flex-shrink-0">
            <Logo className="w-10" />
            <span className="hidden sm:block text-sm font-semibold text-[#1F2933]">Leo Group</span>
          </button>
          <div className="flex-1" />
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-1.5 text-xs text-[#6B7280] hover:text-[#1F2933] border border-[#E5E7EB] hover:border-[#D1D5DB] px-3 py-1.5 rounded transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            Back to Home
          </button>
        </div>
      </nav>

      {/* Content */}
      <main className="flex-1 max-w-4xl mx-auto w-full px-4 sm:px-8 py-12 sm:py-16">

        <div className="mb-10">
          <p className="text-xs font-semibold uppercase tracking-widest text-[#7B1A1A] mb-2">Legal &amp; Compliance</p>
          <h1 className="text-3xl sm:text-4xl font-bold text-[#1F2933] mb-3">POPIA Compliance Statement</h1>
          <p className="text-sm text-[#6B7280]">
            Last updated: 1 May 2026 &nbsp;·&nbsp; Protection of Personal Information Act 4 of 2013
          </p>
        </div>

        {/* Intro card */}
        <div className="bg-[#F0FDF4] border border-[#BBF7D0] rounded-lg px-5 py-4 mb-8 text-sm text-[#166534]">
          <strong>LEO Group Holding Company (Pty) Ltd</strong> is committed to full compliance with the
          <strong> Protection of Personal Information Act 4 of 2013 (POPIA)</strong>, which came into full effect on
          1 July 2021. This Statement outlines how we implement the eight conditions for lawful processing and our
          commitment to protecting the personal information of all data subjects.
        </div>

        <div className="prose prose-sm max-w-none space-y-8 text-[#374151]">

          <Section title="1. About POPIA">
            <p>
              POPIA is South Africa's primary data protection legislation, aligned with international standards including
              the General Data Protection Regulation (GDPR). It regulates how responsible parties (organisations) may
              collect, process, store, share, and destroy personal information. The Act aims to promote the right to
              privacy enshrined in <strong>Section 14 of the Constitution of the Republic of South Africa, 1996</strong>.
            </p>
            <p className="mt-3">Key legislation and regulatory framework:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Protection of Personal Information Act 4 of 2013 (POPIA)</li>
              <li>Constitution of South Africa, Section 14 (Right to Privacy)</li>
              <li>Electronic Communications and Transactions Act 25 of 2002 (ECT Act)</li>
              <li>Cybercrimes Act 19 of 2020</li>
              <li>Information Regulator (South Africa) — established under POPIA Chapter 5</li>
            </ul>
          </Section>

          <Section title="2. Information Officer">
            <p>
              In accordance with <strong>Section 55 of POPIA</strong>, LEO Group has appointed an Information Officer
              who is responsible for:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Ensuring compliance with POPIA across all business functions;</li>
              <li>Handling data subject access requests, complaints, and objections;</li>
              <li>Maintaining the Personal Information Impact Assessment (PIIA);</li>
              <li>Developing, implementing, and monitoring internal privacy policies and procedures;</li>
              <li>Facilitating compliance awareness and training;</li>
              <li>Acting as the primary point of contact with the Information Regulator.</li>
            </ul>
            <address className="not-italic mt-3 text-sm bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg px-4 py-3 space-y-1">
              <p><strong>Information Officer — LEO Group Holding Company (Pty) Ltd</strong></p>
              <p>Republic of South Africa</p>
              <p>Email: <a href="mailto:privacy@leo-group.co.za" className="text-[#7B1A1A] hover:underline">privacy@leo-group.co.za</a></p>
            </address>
          </Section>

          <Section title="3. The Eight Conditions for Lawful Processing">
            <p>
              POPIA prescribes eight conditions that must be satisfied for personal information to be processed lawfully.
              LEO Group complies with each condition as follows:
            </p>

            <div className="space-y-4 mt-4">
              {conditions.map((c, i) => (
                <div key={i} className="flex gap-3 p-4 bg-white border border-[#E5E7EB] rounded-lg">
                  <CheckCircle className="w-4 h-4 text-[#7B1A1A] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-[#1F2933] text-sm">{c.title}</p>
                    <p className="text-xs text-[#6B7280] mt-1 leading-relaxed">{c.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </Section>

          <Section title="4. Personal Information Impact Assessment (PIIA)">
            <p>
              LEO Group conducts and maintains a Personal Information Impact Assessment in accordance with best practice
              and in anticipation of formal regulatory requirements. The PIIA identifies:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>All categories of personal information processed by LEO Group;</li>
              <li>The purposes and lawful basis for each processing activity;</li>
              <li>Applicable retention periods and disposal methods;</li>
              <li>Third-party operators and data flows (including cross-border transfers);</li>
              <li>Risks to data subjects and the controls in place to mitigate those risks.</li>
            </ul>
          </Section>

          <Section title="5. Data Subject Rights">
            <p>
              LEO Group respects and upholds the rights of all data subjects as provided for in
              <strong> Sections 23–25 of POPIA</strong>. Data subjects may, at any time:
            </p>
            <ul className="list-disc pl-5 space-y-2">
              <li>
                <strong>Request access</strong> to their personal information held by LEO Group — we will respond
                within 30 days;
              </li>
              <li>
                <strong>Request correction or deletion</strong> of inaccurate, incomplete, irrelevant, excessive,
                out-of-date, misleading, or unlawfully obtained personal information;
              </li>
              <li>
                <strong>Object to processing</strong> on reasonable grounds, after which we will no longer process
                the information unless we can demonstrate compelling legitimate grounds;
              </li>
              <li>
                <strong>Withdraw consent</strong> at any time where processing is based on consent, without affecting
                the lawfulness of prior processing;
              </li>
              <li>
                <strong>Submit a complaint</strong> to the Information Regulator if they believe their POPIA rights
                have been infringed.
              </li>
            </ul>
            <p className="mt-3">
              All requests must be submitted in writing to our Information Officer at
              <a href="mailto:privacy@leo-group.co.za" className="text-[#7B1A1A] hover:underline ml-1">privacy@leo-group.co.za</a>.
            </p>
          </Section>

          <Section title="6. Security Measures (Section 19)">
            <p>
              In fulfilment of our obligations under <strong>Section 19 of POPIA</strong>, LEO Group has implemented
              the following technical and organisational security measures:
            </p>
            <div className="grid sm:grid-cols-2 gap-3 mt-3">
              {securityMeasures.map((m, i) => (
                <div key={i} className="flex items-start gap-2 text-xs bg-white border border-[#E5E7EB] rounded px-3 py-2.5">
                  <CheckCircle className="w-3.5 h-3.5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-[#374151]">{m}</span>
                </div>
              ))}
            </div>
          </Section>

          <Section title="7. Data Breach Notification (Section 22)">
            <p>
              LEO Group has incident response procedures in place to detect, contain, and respond to personal information
              breaches. In the event of a breach that poses a risk to a data subject:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>The Information Regulator will be notified as soon as reasonably possible;</li>
              <li>Affected data subjects will be notified as soon as reasonably possible;</li>
              <li>Notification will include the nature of the breach, categories of information involved, and steps taken to mitigate harm;</li>
              <li>A breach register is maintained internally for audit purposes.</li>
            </ul>
          </Section>

          <Section title="8. Operators and Third Parties">
            <p>
              Where LEO Group engages operators (third-party service providers) to process personal information on its
              behalf, we ensure that:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Written data processing agreements are in place in accordance with <strong>Section 21 of POPIA</strong>;</li>
              <li>Operators are required to implement appropriate security measures;</li>
              <li>Operators are prohibited from processing personal information for purposes other than those instructed by LEO Group;</li>
              <li>Operators are required to notify LEO Group immediately of any suspected or actual personal information breach.</li>
            </ul>
          </Section>

          <Section title="9. Retention and Disposal">
            <p>
              Personal information is retained only for as long as necessary to fulfil the purpose of collection or
              as required by law. Once the retention period has expired, personal information is securely deleted,
              anonymised, or destroyed in a manner that prevents reconstruction. Retention periods are documented
              in our PIIA and data retention schedule.
            </p>
          </Section>

          <Section title="10. Complaints Procedure">
            <p>
              If you believe that LEO Group has processed your personal information in contravention of POPIA, you may:
            </p>
            <ol className="list-decimal pl-5 space-y-1">
              <li>Submit a written complaint to our Information Officer at <a href="mailto:privacy@leo-group.co.za" className="text-[#7B1A1A] hover:underline">privacy@leo-group.co.za</a> — we will acknowledge within 5 business days and respond within 30 days;</li>
              <li>If unsatisfied with our response, lodge a complaint with the Information Regulator of South Africa.</li>
            </ol>
            <address className="not-italic mt-3 text-sm bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg px-4 py-3 space-y-1">
              <p><strong>Information Regulator (South Africa)</strong></p>
              <p>JD House, 27 Stiemens Street, Braamfontein, Johannesburg, 2001</p>
              <p>Complaints email: <a href="mailto:PAIAComplaints@inforegulator.org.za" className="text-[#7B1A1A] hover:underline">PAIAComplaints@inforegulator.org.za</a></p>
              <p>General enquiries: <a href="mailto:inforeg@justice.gov.za" className="text-[#7B1A1A] hover:underline">inforeg@justice.gov.za</a></p>
              <p>Website: <a href="https://www.justice.gov.za/inforeg/" target="_blank" rel="noopener noreferrer" className="text-[#7B1A1A] hover:underline">www.justice.gov.za/inforeg</a></p>
            </address>
          </Section>

          <Section title="11. Updates to This Statement">
            <p>
              This POPIA Compliance Statement is reviewed and updated at least annually, or whenever there is a material
              change to our data processing activities or applicable legislation. The "Last updated" date at the top of
              this page reflects the most recent revision.
            </p>
          </Section>

        </div>
      </main>

      <Footer />
    </div>
  );
}

// ─── Data ─────────────────────────────────────────────────────────────────────

const conditions = [
  {
    title: "Condition 1 — Accountability",
    description: "LEO Group, as the Responsible Party, takes responsibility for all personal information in its possession or under its control. An appointed Information Officer oversees POPIA compliance across the organisation.",
  },
  {
    title: "Condition 2 — Processing Limitation",
    description: "Personal information is collected only for a specific, explicitly defined, and lawful purpose. We do not process personal information beyond what is adequate, relevant, and not excessive relative to that purpose.",
  },
  {
    title: "Condition 3 — Purpose Specification",
    description: "Personal information is collected for specific, explicitly defined, and lawful purposes. Data subjects are informed of the purpose at the time of collection and information is not retained longer than necessary.",
  },
  {
    title: "Condition 4 — Further Processing Limitation",
    description: "Personal information is not processed for a secondary purpose unless that purpose is compatible with the original purpose, the data subject has consented, or a legal requirement permits further processing.",
  },
  {
    title: "Condition 5 — Information Quality",
    description: "LEO Group takes reasonable steps to ensure that personal information is complete, accurate, not misleading, and updated where necessary. Data subjects may request correction of inaccurate information.",
  },
  {
    title: "Condition 6 — Openness",
    description: "LEO Group maintains documentation of all processing activities. Data subjects are notified of collection and may access this Privacy Policy and POPIA Statement at any time.",
  },
  {
    title: "Condition 7 — Security Safeguards",
    description: "Appropriate technical and organisational measures are implemented to prevent loss, damage, or unauthorised access to personal information. A breach notification procedure is in place in terms of Section 22 of POPIA.",
  },
  {
    title: "Condition 8 — Data Subject Participation",
    description: "Data subjects may request access to, correction, or deletion of their personal information, and may object to processing. All requests are handled by the Information Officer within 30 days.",
  },
];

const securityMeasures = [
  "TLS/HTTPS encryption for all data in transit",
  "AES-256 encryption for sensitive fields at rest",
  "Token-based access control with session expiry",
  "Role-based access controls (RBAC)",
  "Comprehensive audit logging",
  "Access restricted on a need-to-know basis",
  "Regular security reviews and vulnerability assessments",
  "Secure development practices (OWASP guidelines)",
  "Incident response and breach notification procedures",
  "Data minimisation — only necessary data collected",
];

// ─── Shared sub-components ────────────────────────────────────────────────────

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h2 className="text-base font-semibold text-[#1F2933] mb-3 pb-2 border-b border-[#E5E7EB]">{title}</h2>
      <div className="space-y-3 text-sm leading-relaxed">{children}</div>
    </div>
  );
}

function Footer() {
  const navigate = useNavigate();
  return (
    <footer className="bg-[#6b1515] mt-16">
      <div className="max-w-5xl mx-auto px-4 sm:px-8 py-8">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Logo className="w-10" white />
            <span className="text-sm font-medium text-white">Leo Group</span>
          </div>
          <div className="flex items-center gap-4 text-xs text-white/60">
            <button onClick={() => navigate("/terms")} className="hover:text-white transition-colors">Terms</button>
            <button onClick={() => navigate("/privacy")} className="hover:text-white transition-colors">Privacy Policy</button>
            <button onClick={() => navigate("/compliance")} className="hover:text-white transition-colors">POPIA</button>
          </div>
        </div>
        <div className="border-t border-white/10 mt-6 pt-5 text-center text-xs text-white/30">
          © 2026 LEO Group Holding Company. All rights reserved.
        </div>
      </div>
    </footer>
  );
}
